import { useCallback } from 'react';
import { ExecutionObserverConfig, SavedObserverConfig } from '../models/ExecutionObserverConfig';

// Revoking right after click() can race the browser's save and corrupt the download;
// keep the blob URL alive long enough for the download to start.
const BLOB_URL_REVOKE_DELAY_MS = 2000;

const STORAGE_KEYS = {
  current: 'flow-studio:observer:current-config',
  saved: 'flow-studio:observer:saved-configs',
  recent: 'flow-studio:observer:recent-configs',
};

/**
 * Hook for managing observer configuration preferences with localStorage persistence
 */
export const useObserverPreferences = () => {
  const saveConfig = useCallback(
    (config: ExecutionObserverConfig) => {
      try {
        localStorage.setItem(STORAGE_KEYS.current, JSON.stringify(config));
      } catch (error) {
        console.error('Failed to save observer config:', error);
      }
    },
    []
  );

  const loadConfig = useCallback((): ExecutionObserverConfig | null => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.current);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Failed to load observer config:', error);
      return null;
    }
  }, []);

  const saveAsPreference = useCallback(
    (name: string, config: ExecutionObserverConfig) => {
      try {
        const saved: SavedObserverConfig[] = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.saved) || '[]'
        );

        const newConfig: SavedObserverConfig = {
          id: `cfg-${Date.now()}`,
          name,
          description: '',
          config,
          createdAt: new Date(),
          lastUsedAt: new Date(),
          useCount: 0,
        };

        saved.push(newConfig);
        localStorage.setItem(STORAGE_KEYS.saved, JSON.stringify(saved));
        addToRecent(newConfig);
      } catch (error) {
        console.error('Failed to save preference:', error);
      }
    },
    []
  );

  const loadPreference = useCallback((id: string): ExecutionObserverConfig | null => {
    try {
      const saved: SavedObserverConfig[] = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.saved) || '[]'
      );
      const cfg = saved.find((c) => c.id === id);
      if (cfg) {
        cfg.lastUsedAt = new Date();
        cfg.useCount++;
        localStorage.setItem(STORAGE_KEYS.saved, JSON.stringify(saved));
        addToRecent(cfg);
        return cfg.config;
      }
      return null;
    } catch (error) {
      console.error('Failed to load preference:', error);
      return null;
    }
  }, []);

  const deletePreference = useCallback((id: string) => {
    try {
      const saved: SavedObserverConfig[] = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.saved) || '[]'
      );
      const filtered = saved.filter((c) => c.id !== id);
      localStorage.setItem(STORAGE_KEYS.saved, JSON.stringify(filtered));
    } catch (error) {
      console.error('Failed to delete preference:', error);
    }
  }, []);

  const getSavedConfigs = useCallback((): SavedObserverConfig[] => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.saved) || '[]');
    } catch (error) {
      console.error('Failed to get saved configs:', error);
      return [];
    }
  }, []);

  const getRecentConfigs = useCallback((): SavedObserverConfig[] => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.recent) || '[]');
    } catch (error) {
      console.error('Failed to get recent configs:', error);
      return [];
    }
  }, []);

  const addToRecent = (config: SavedObserverConfig) => {
    try {
      const recent: SavedObserverConfig[] = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.recent) || '[]'
      );
      recent.unshift(config);
      if (recent.length > 5) recent.pop();
      localStorage.setItem(STORAGE_KEYS.recent, JSON.stringify(recent));
    } catch (error) {
      console.error('Failed to add to recent:', error);
    }
  };

  const exportConfig = useCallback((config: ExecutionObserverConfig) => {
    const data = JSON.stringify(config, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `observer-config-${Date.now()}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
  }, []);

  const importConfig = useCallback(
    (file: File): Promise<ExecutionObserverConfig | null> => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const config = JSON.parse(e.target?.result as string);
            resolve(config);
          } catch (error) {
            console.error('Failed to import config:', error);
            resolve(null);
          }
        };
        reader.readAsText(file);
      });
    },
    []
  );

  const updateConfig = useCallback((config: ExecutionObserverConfig) => {
    saveConfig(config);
  }, [saveConfig]);

  const resetToDefault = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.current);
  }, []);

  return {
    saveConfig,
    loadConfig,
    saveAsPreference,
    loadPreference,
    deletePreference,
    getSavedConfigs,
    getRecentConfigs,
    updateConfig,
    resetToDefault,
    exportConfig,
    importConfig,
  };
};

export default useObserverPreferences;
